import React, { useRef, useState } from 'react';
import { FaSort } from 'react-icons/fa';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  PieLabelRenderProps,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { CHART_SERIES_COLORS } from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';
import { useSeriesSelection } from '@/features/ai-agents/chat/MarkdownTableVisualization/use-series-selection';
import { isPercentageMetric } from '@/features/ai-agents/chat/MarkdownTableVisualization/helpers';
import {
  ComparePieLayout,
  ComparePiePanel,
  ComparePieTitle,
  PieChartArea,
  PieChartCenter,
  PieChartCenterLabel,
  PieChartCenterLabelWrapper,
  PieChartCenterTooltip,
  PieChartCenterValue,
  PieChartLayout,
  PieChartSizeQuery,
  PieLegend,
  PieLegendHeader,
  PieLegendHeaderLabel,
  PieLegendList,
  PieLegendName,
  PieLegendNameCell,
  PieLegendRow,
  PieLegendShare,
  PieLegendSortButton,
  PieLegendSortIndicator,
  PieLegendSwatch,
  PieLegendValue,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import {
  ChartRow,
  ChartValueFormatter,
  SortDirection,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  excludeTotalRows,
  formatColumnHeader,
  isTotalRow,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

const PIE_SLICE_DIMMED_OPACITY = 0.35;
const PIE_SLICE_ACTIVE_STROKE_WIDTH = 2;
const PIE_SLICE_LABEL_MIN_PERCENT = 0.05;
const PIE_CHART_MAX_SLICES = 10;
const PIE_CHART_OTHER_LABEL = `Other`;
const PIE_CHART_OTHER_COLOR = `#9ca3af`;

export interface ComparePieData {
  compare: ChartRow[];
  compareLabel: string;
  current: ChartRow[];
}

interface PieChartProps {
  compareData?: ComparePieData | null;
  data: ChartRow[];
  formatValue: ChartValueFormatter;
  isPercentageValue?: boolean;
  totalLabel?: string;
  valueLabel?: string;
}

function renderPieSlicePercentLabel({
  cx,
  cy,
  innerRadius,
  midAngle,
  outerRadius,
  percent,
}: PieLabelRenderProps) {
  if (
    !percent ||
    percent < PIE_SLICE_LABEL_MIN_PERCENT ||
    midAngle === undefined
  ) {
    return null;
  }

  const RADIAN = Math.PI / 180;
  const numericCx = Number(cx);
  const numericCy = Number(cy);
  const numericInnerRadius = Number(innerRadius);
  const numericOuterRadius = Number(outerRadius);
  const labelRadius =
    numericInnerRadius + (numericOuterRadius - numericInnerRadius) / 2;
  const x = numericCx + labelRadius * Math.cos(-midAngle * RADIAN);
  const y = numericCy + labelRadius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      fontSize={12}
      fontWeight={600}
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
}

/**
 * When the table already provides a pre-aggregated "Total"/"All" row, its
 * value is the true grand total. Re-summing the remaining rows would be
 * wrong whenever those rows span more than one breakdown dimension (e.g.
 * device type and customer type rows mixed in the same column), since each
 * dimension's rows already add up to the same whole on their own.
 */
function findPieChartTotalRowValue(data: ChartRow[]) {
  return data.find(isTotalRow)?.y ?? null;
}

function sumChartRowValues(data: ChartRow[]) {
  return data.reduce((total, row) => total + row.y, 0);
}

function groupPieChartSlicesIntoTopAndOther(data: ChartRow[]) {
  if (data.length <= PIE_CHART_MAX_SLICES) {
    return data;
  }

  const sortedByValueDescending = [...data].sort((a, b) => b.y - a.y);
  const topSlices = sortedByValueDescending.slice(0, PIE_CHART_MAX_SLICES);
  const otherSlices = sortedByValueDescending.slice(PIE_CHART_MAX_SLICES);
  const otherSlice: ChartRow = {
    formattedY: ``,
    x: PIE_CHART_OTHER_LABEL,
    y: sumChartRowValues(otherSlices),
  };

  return [...topSlices, otherSlice];
}

function getPieSliceColor(row: { x: string }, index: number) {
  return row.x === PIE_CHART_OTHER_LABEL
    ? PIE_CHART_OTHER_COLOR
    : CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length];
}

function getNextValueSortDirection(
  direction: SortDirection | null,
): SortDirection | null {
  if (direction === `descending`) {
    return `ascending`;
  }

  return direction === `ascending` ? null : `descending`;
}

function sortLegendEntriesByValue<T extends { y: number }>(
  entries: T[],
  direction: SortDirection | null,
) {
  if (!direction) {
    return entries;
  }

  const sign = direction === `ascending` ? 1 : -1;

  return [...entries].sort((a, b) => sign * (a.y - b.y));
}

export function PieChart({
  compareData,
  data,
  formatValue,
  isPercentageValue,
  totalLabel,
  valueLabel,
}: PieChartProps) {
  if (compareData) {
    return (
      <ComparePieLayout>
        <ComparePiePanel>
          <ComparePieTitle>{compareData.compareLabel}</ComparePieTitle>
          <SinglePieChart
            data={compareData.compare}
            formatValue={formatValue}
            isPercentageValue={isPercentageValue}
            totalLabel={totalLabel}
            valueLabel={valueLabel}
          />
        </ComparePiePanel>
        <ComparePiePanel>
          <ComparePieTitle>Current</ComparePieTitle>
          <SinglePieChart
            data={compareData.current}
            formatValue={formatValue}
            isPercentageValue={isPercentageValue}
            totalLabel={totalLabel}
            valueLabel={valueLabel}
          />
        </ComparePiePanel>
      </ComparePieLayout>
    );
  }

  return (
    <SinglePieChart
      data={data}
      formatValue={formatValue}
      isPercentageValue={isPercentageValue}
      totalLabel={totalLabel}
      valueLabel={valueLabel}
    />
  );
}

interface SinglePieChartProps {
  data: ChartRow[];
  formatValue: ChartValueFormatter;
  isPercentageValue?: boolean;
  totalLabel?: string;
  valueLabel?: string;
}

function SinglePieChart({
  data,
  formatValue,
  isPercentageValue,
  totalLabel,
  valueLabel,
}: SinglePieChartProps) {
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [valueSortDirection, setValueSortDirection] =
    useState<SortDirection | null>(null);
  const groupedData = groupPieChartSlicesIntoTopAndOther(
    excludeTotalRows(data),
  );
  const sliceSelection = useSeriesSelection(
    groupedData.map((_row, index) => String(index)),
  );
  const visibleSlices = groupedData
    .map((row, originalIndex) => ({ ...row, originalIndex }))
    .filter((row) => sliceSelection.isVisible(String(row.originalIndex)));
  const isFilteredBySelection = visibleSlices.length < groupedData.length;
  const total = isFilteredBySelection
    ? sumChartRowValues(visibleSlices)
    : findPieChartTotalRowValue(data) ?? sumChartRowValues(groupedData);
  // Percentage-based metrics (e.g. conversion rate) are not summable across
  // categories, so showing their total would be misleading.
  const canShowTotal =
    total > 0 &&
    (isPercentageValue ?? isPercentageMetric(valueLabel ?? ``)) === false;
  const formatSlice = (row: ChartRow & { originalIndex: number }) => ({
    ...row,
    displayName: formatColumnHeader(row.x),
    formattedY: formatValue(row.y),
    sharePercent: total ? (row.y / total) * 100 : 0,
  });
  const formattedData = visibleSlices.map(formatSlice);
  const legendEntries = sortLegendEntriesByValue(
    groupedData.map((row, originalIndex) => ({
      ...formatSlice({ ...row, originalIndex }),
      isVisible: sliceSelection.isVisible(String(originalIndex)),
    })),
    valueSortDirection,
  );

  const dispatchPieSliceHover = (
    index: number,
    type: `mouseover` | `mouseout`,
  ) => {
    const sectorPosition = formattedData.findIndex(
      (row) => row.originalIndex === index,
    );
    const sector =
      chartAreaRef.current?.querySelectorAll(`.recharts-pie-sector`)[
        sectorPosition
      ];

    sector?.dispatchEvent(new MouseEvent(type, { bubbles: true }));
  };

  return (
    <PieChartSizeQuery className="pie-chart-size-query">
      <style>
        {`
          .pie-chart-size-query {
            container-type: inline-size;
            container-name: pie-chart;
          }

          .pie-chart-area {
            flex-shrink: 1;
          }

          .pie-legend {
            flex-shrink: 1;
          }

          @container pie-chart (max-width: 58rem) {
            .pie-chart-layout {
              flex-direction: column;
              justify-content: flex-start;
              height: auto;
              min-height: 100%;
            }

            .pie-legend {
              width: 100%;
              min-width: 0;
              max-width: none;
              height: auto;
              overflow-y: visible;
              padding: 1rem 0 0;
            }
          }
        `}
      </style>
      <PieChartLayout className="pie-chart-layout">
        <PieChartArea className="pie-chart-area" ref={chartAreaRef}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Tooltip
                formatter={formatValue}
                wrapperStyle={{ zIndex: 9999 }}
              />
              <Pie
                data={formattedData}
                dataKey="y"
                nameKey="displayName"
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={120}
                paddingAngle={2}
                cornerRadius={3}
                isAnimationActive={false}
                label={renderPieSlicePercentLabel}
                labelLine={false}
                onMouseEnter={(_, index) =>
                  setActiveIndex(formattedData[index]?.originalIndex ?? null)
                }
                onMouseLeave={() => setActiveIndex(null)}
              >
                {formattedData.map((row) => {
                  const isActive = activeIndex === row.originalIndex;
                  const isDimmed = activeIndex !== null && !isActive;

                  return (
                    <Cell
                      key={`${row.x}-${row.originalIndex}`}
                      fill={getPieSliceColor(row, row.originalIndex)}
                      fillOpacity={isDimmed ? PIE_SLICE_DIMMED_OPACITY : 1}
                      stroke="#fff"
                      strokeWidth={isActive ? PIE_SLICE_ACTIVE_STROKE_WIDTH : 0}
                      style={{
                        transition: `fill-opacity 150ms ease, stroke-width 150ms ease`,
                      }}
                    />
                  );
                })}
              </Pie>
            </RechartsPieChart>
          </ResponsiveContainer>
          {canShowTotal && (
            <PieChartCenter>
              <PieChartCenterValue>{formatValue(total)}</PieChartCenterValue>
              {totalLabel && (
                <PieChartCenterLabelWrapper>
                  <PieChartCenterLabel>{totalLabel}</PieChartCenterLabel>
                  <PieChartCenterTooltip role="tooltip">
                    {totalLabel}
                  </PieChartCenterTooltip>
                </PieChartCenterLabelWrapper>
              )}
            </PieChartCenter>
          )}
        </PieChartArea>
        <PieLegend className="pie-legend">
          <PieLegendHeader>
            <PieLegendHeaderLabel />
            <PieLegendSortButton
              type="button"
              title="Sort by value"
              onClick={() =>
                setValueSortDirection(
                  getNextValueSortDirection(valueSortDirection),
                )
              }
            >
              Value
              <PieLegendSortIndicator aria-hidden>
                {valueSortDirection === `ascending` && <FiArrowUp />}
                {valueSortDirection === `descending` && <FiArrowDown />}
                {!valueSortDirection && <FaSort />}
              </PieLegendSortIndicator>
            </PieLegendSortButton>
            <PieLegendHeaderLabel>Share</PieLegendHeaderLabel>
          </PieLegendHeader>
          <PieLegendList>
            {legendEntries.map((row) => (
              <PieLegendRow
                key={`${row.x}-${row.originalIndex}`}
                $dimmed={
                  !row.isVisible ||
                  (activeIndex !== null && activeIndex !== row.originalIndex)
                }
                onClick={() => sliceSelection.toggle(String(row.originalIndex))}
                onMouseEnter={() =>
                  dispatchPieSliceHover(row.originalIndex, `mouseover`)
                }
                onMouseLeave={() =>
                  dispatchPieSliceHover(row.originalIndex, `mouseout`)
                }
              >
                <PieLegendNameCell>
                  <PieLegendSwatch
                    $color={getPieSliceColor(row, row.originalIndex)}
                  />
                  <PieLegendName title={row.displayName}>
                    {row.displayName}
                  </PieLegendName>
                </PieLegendNameCell>
                <PieLegendValue>{row.formattedY}</PieLegendValue>
                <PieLegendShare>{`${row.sharePercent.toFixed(
                  1,
                )}%`}</PieLegendShare>
              </PieLegendRow>
            ))}
          </PieLegendList>
        </PieLegend>
      </PieChartLayout>
    </PieChartSizeQuery>
  );
}
